import { Actions } from '@wahaaabello/redux-async-actions';
import { LocationDescriptorObject } from 'history';
import React, {
  forwardRef,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react';
import { useAsync } from 'react-async';
import { useDispatch, useSelector } from 'react-redux';
import {
  Link,
  Route,
  useHistory,
  useLocation,
  useRouteMatch
} from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';
import { TEMPLATE_PAGE_SEPARATOR } from '../../constants';
import PageDataContext from '../../contexts/page-data';
import ProductColorContext from '../../contexts/product-color';
import { appendPageDataToPageId, urlsMatch, isURL } from '../../helpers/utils';
import { ExtraLocationDescriptorOptions } from '../../routes';
import formsActions from '../../store/modules/forms/actions';
import { getFormInfo } from '../../store/modules/forms/reducer';
import pagesActions from '../../store/modules/pages/actions';
import { getPageInfo } from '../../store/modules/pages/reducer';
import { RootState, Styled } from '../../types';
import Builder from '../Builder';
import { PageBuilderProps } from '../Builder/PageBuilder';
import LightBox from '../LightBox';
import Loader from '../Loader';
import once from 'lodash/once';

export type ModalLinkProps = Styled & {
  to: ExtraLocationDescriptorOptions & LocationDescriptorObject;
  children?: ReactNode;
};

const ModalLink = forwardRef<any, ModalLinkProps>(
  ({ to, children = null, ...restProps }, ref) => {
    const dispatch = useDispatch<
      ThunkDispatch<RootState, undefined, Actions>
    >();
    const match = useRouteMatch();
    const history = useHistory();
    const location = useLocation();
    const { reload: upperReload } = useContext(PageDataContext);
    const color = useContext(ProductColorContext);
    const [isOpen, setIsOpen] = useState(
      urlsMatch(location.pathname, to.pathname ?? '')
    );
    const isInnerModal = useMemo(
      () =>
        !!to.pathname &&
        to.pathname.split(`/${TEMPLATE_PAGE_SEPARATOR}/`).length > 2,
      [to.pathname]
    );
    const { pagedata: pageData } =
      useSelector(
        getPageInfo(
          to.state?.pageId
            ? appendPageDataToPageId(to.state.pageId, to.state)
            : ''
        )
      ) ?? {};
    const [headerTitle, setHeaderTitle] = useState(
      to.modal?.headerTitle ?? to.state?.title ?? ''
    );
    const [headerDescription, setHeaderDescription] = useState(
      to.modal?.headerDescription ?? to.state?.subtitle ?? ''
    );
    useEffect(() => {
      setHeaderTitle(to.modal?.headerTitle ?? to.state?.title ?? '');
      setHeaderDescription(
        to.modal?.headerDescription ?? to.state?.subtitle ?? ''
      );
    }, [to.modal, to.state]);
    const { formfields: formInfo } =
      useSelector(getFormInfo(to.state?.formId)) ?? {};
    const rawPageData = useSelector(to.methods?.getRawPageData ?? (() => null));
    useEffect(() => {
      if (to.methods?.getHeader) {
        const { title = '', description = '' } = to.methods.getHeader(
          rawPageData ?? {}
        );
        if (title) setHeaderTitle(title);
        if (description) setHeaderDescription(description);
      }
    }, [to.methods, setHeaderTitle, setHeaderDescription, rawPageData]);

    const [formData, setFormData] = useState(null);
    const loadPage = useCallback(
      () => dispatch(pagesActions.fetchPageRequest(to.state)),
      [dispatch, to.state]
    );
    const loadForm = useCallback(
      () => dispatch(formsActions.fetchFormRequest(to.state?.formId)),
      [dispatch, to.state]
    );
    const {
      isLoading: isLoadingPage,
      run: runLoadPage,
      reload: reloadLoadPage
    } = useAsync<any>({ deferFn: loadPage });
    const {
      isLoading: isLoadingForm,
      run: runLoadForm,
      reload: reloadLoadForm
    } = useAsync({ deferFn: loadForm });
    const {
      isLoading: isLoadingPageData,
      run: runLoadPageData,
      reload: reloadLoadPageData
    } = useAsync({ deferFn: to.methods?.loadRawPageData });

    const { current: run } = useRef(
      once(() => {
        if (to.state?.pageId) {
          runLoadPage();
        } else if (to.state?.formId) {
          runLoadForm();
        } else if (to.methods?.loadRawPageData) {
          runLoadPageData();
        }
      })
    );

    const reload = useCallback(() => {
      if (to.state?.pageId) {
        reloadLoadPage();
      } else if (to.state?.formId) {
        reloadLoadForm();
      } else if (to.methods?.loadRawPageData) {
        reloadLoadPageData();
      }
      upperReload?.();
    }, [
      reloadLoadPage,
      reloadLoadForm,
      reloadLoadPageData,
      upperReload,
      to.state,
      to.methods
    ]);

    useEffect(() => {
      if (isOpen && to.state?.modal) {
        run();
      }
    }, [isOpen, run, to.state]);

    const handleLinkClick = useCallback(() => {
      setIsOpen(true);
    }, []);

    const handleClose = useCallback(() => {
      setIsOpen(false);
      setTimeout(() => {
        history.replace(match.url);
      }, 250);
    }, [history, match.url]);

    useEffect(() => {
      if (!formInfo) {
        return;
      }
      setFormData(formInfo);
    }, [formInfo]);

    const renderPageBuilder = useCallback(
      dPageData => {
        const PageBuilder = Builder.Page as FunctionComponent<PageBuilderProps>;
        return <PageBuilder pageData={dPageData} handleClose={handleClose} />;
      },
      [handleClose]
    );

    const renderFormBuilder = useCallback(
      (dFormData, dFormProps = {}) => {
        const FormBuilder = Builder.Form as FunctionComponent<any>;
        return (
          <FormBuilder
            formFields={dFormData}
            containerClassName="mx-6 md:mx-10 mb-6 md:mb-10"
            submit={(values: { [name: string]: any }) => {
              if (to.methods?.submit) {
                return to.methods.submit(values, { handleClose });
              }
              return null;
            }}
            {...dFormProps}
          />
        );
      },
      [to.methods, handleClose]
    );

    const renderModalContent = useCallback(() => {
      if (to.modal) {
        if (to.modal.content) return to.modal.content;
        if (to.modal.pageData) {
          return renderPageBuilder(to.modal.pageData);
        }
        if (to.modal.formData) {
          return renderFormBuilder(to.modal.formData, to.modal.formProps);
        }
      }
      if (!pageData && !formData && !rawPageData) {
        if (isLoadingPage || isLoadingForm || isLoadingPageData) {
          return <Loader />;
        }
        const LoaderError = Loader.Error;
        return <LoaderError reload={run} />;
      }
      if (pageData) {
        return renderPageBuilder(pageData);
      }
      if (rawPageData) {
        return renderPageBuilder(
          to.methods?.transformRawPageData?.(rawPageData)
        );
      }
      if (formData) {
        return renderFormBuilder(formData);
      }
      return null;
    }, [
      isLoadingPage,
      isLoadingForm,
      isLoadingPageData,
      pageData,
      rawPageData,
      formData,
      to.modal,
      to.methods,
      run,
      renderFormBuilder,
      renderPageBuilder
    ]);
    if (isURL(to?.pathname ?? '')) {
      return (
        <a
          href={to.pathname}
          target="__blank"
          rel="noreferrer noopener"
          {...restProps}
        >
          {children}
        </a>
      );
    }
    return (
      <>
        <Link
          to={to}
          {...restProps}
          onClick={handleLinkClick}
          replace={!!to.state?.modal}
          ref={ref}
        >
          {children}
        </Link>
        {to.state?.modal && (
          <Route
            path={to.pathname ?? ((to as unknown) as string)}
            render={() => (
              <PageDataContext.Provider value={{ reload }}>
                <ProductColorContext.Provider
                  value={pageData?.color ?? to.modal?.headerColor ?? color}
                >
                  <LightBox
                    isOpen={isOpen}
                    onClose={handleClose}
                    headerTitle={headerTitle}
                    headerDescription={headerDescription}
                    headerColor={
                      pageData?.color ?? to.modal?.headerColor ?? color
                    }
                    content={renderModalContent()}
                    useBack={isInnerModal}
                    showOverlayBackgroundColor={!isInnerModal}
                    id={`${
                      to.state?.pageId ? `${to.state.pageId}-` : ''
                    }modal-container`}
                  />
                </ProductColorContext.Provider>
              </PageDataContext.Provider>
            )}
          />
        )}
      </>
    );
  }
);

export default ModalLink;
